import { PlayCircle, Film } from "lucide-react";
import { Modal, ModalScroll } from "./Modal";
import { SmartGallery } from "./SmartGallery";
import { FramedImage } from "./FramedImage";
import { AwardLabel, filmAward, NameLink } from "./FestivalGraphics";
import { posterAspectKey } from "./FestivalShowcase";
import { MAXW_CLASS } from "../../lib/frame";

// Detailed film card shown as a MODAL — reuses the exact styling language of the
// Réalisations detail (ProjectDetail) but adapted to a Festival film. Stays on the
// Festival page so the visitor returns to the same row/position when closing.
// CADRAGE UNIFIÉ : film.posterFrame (Mode Transformer) pilote le conteneur lui-même
// (format vertical/horizontal/carré/proportions d'origine, fond, bordure, largeur max).
export const FestivalFilmModal = ({ film, editionYear, onClose }) => {
  const media = film ? (Array.isArray(film.media) && film.media.length ? film.media : (film.poster ? [film.poster] : [])) : [];
  const aw = film ? filmAward(film) : null;
  const pf = film?.posterFrame || {};
  const dd = film?.detail || {}; // repli legacy (anciennes données)
  const legacyKey = posterAspectKey(film?.posterFormat);
  const fmt = pf.format && pf.format !== "auto" ? pf.format : (dd.aspect && dd.aspect !== "auto" ? dd.aspect : legacyKey);
  const fmtMap = { vertical: "aspect-[2/3]", portrait: "aspect-[2/3]", horizontal: "aspect-video", landscape: "aspect-video", square: "aspect-square" };
  const intrinsic = fmt === "original";
  const frameClass = intrinsic ? null : (fmtMap[fmt] || "aspect-video");
  const frameSrc = Object.keys(pf).length ? pf : { fit: dd.fit, zoom: dd.zoom, posX: dd.posX, posY: dd.posY };
  const contain = frameSrc.fit === "contain" || intrinsic;
  const imgFit = contain ? "object-contain" : "object-cover";
  const imgStyle = { transform: `scale(${frameSrc.zoom ?? 1})`, objectPosition: `${frameSrc.posX ?? 50}% ${frameSrc.posY ?? 50}%` };
  const frameMax = MAXW_CLASS[pf.maxW] || ((intrinsic || fmt === "vertical" || fmt === "portrait") ? "max-w-sm mx-auto" : fmt === "square" ? "max-w-md mx-auto" : "");
  const singleImage = media.length === 1 && (typeof media[0] === "string" || media[0]?.type !== "video");
  const info = film ? [
    ["Édition", editionYear ? `Karams Festival ${editionYear}` : film.edition],
    ...(film.director ? [["Réalisation", <NameLink name={film.director} url={film.directorUrl} />]] : []),
    ...(film.team ? [["Structure / Production", <NameLink name={film.team} url={film.teamUrl} />]] : []),
    ["Durée", film.duration],
    ["Format", film.format],
    ["Genre", film.genre],
    ["Thème", film.theme],
    ["Catégorie", film.category],
  ].filter(([, v]) => v) : [];

  return (
    <Modal open={!!film} onClose={onClose} size="2xl" testid="film-modal">
      {film && (
        <ModalScroll testid="film-modal-scroll">
          <div className="p-6 md:p-10">
            <div className="text-[11px] tracking-[0.3em] uppercase text-electric mb-3 pr-10">
              {editionYear ? `Karams Festival ${editionYear}` : "Karams Festival"}
            </div>
            <h2 data-testid="film-modal-title" className="font-serif text-4xl md:text-5xl text-white tracking-tight leading-[0.98] mb-5">{film.title}</h2>

            {aw && (
              <div className="mb-6" data-testid="film-modal-award">
                <AwardLabel name={aw.name} color={aw.color} size={Math.min(aw.size * 1.15, 1.5)} />
                {aw.juryNote && (
                  <blockquote className="mt-4 border-l-2 border-electric/50 pl-4 text-white/80 font-light italic leading-relaxed" data-testid="film-modal-jury-note">
                    <span className="not-italic block text-[10px] tracking-[0.25em] uppercase text-electric mb-1.5">Mot du jury</span>
                    {aw.juryNote}
                  </blockquote>
                )}
              </div>
            )}

            {media.length > 0 && (
              <div className={`mb-8 ${frameMax}`}>
                {intrinsic && singleImage
                  ? <FramedImage src={media[0]?.url || media[0]} frame={frameSrc} alt={film.title} testid="film-poster-intrinsic" />
                  : <SmartGallery images={media} options={film.gallerySettings} mode={film.gallerySettings ? undefined : "slide"} label="" frameClass={frameClass || "aspect-video"} imgFit={imgFit} imgStyle={imgStyle} />}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
              <div>
                {film.synopsis && (
                  <>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-3">Synopsis</div>
                    <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed whitespace-pre-line mb-8">{film.synopsis}</p>
                  </>
                )}
                {(film.filmUrl || film.trailerUrl) && (
                  <div className="flex flex-wrap gap-3" data-testid="film-modal-links">
                    {film.filmUrl && <a href={film.filmUrl} target="_blank" rel="noreferrer" data-testid="film-watch-link" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors"><PlayCircle size={16} /> Voir le film</a>}
                    {film.trailerUrl && <a href={film.trailerUrl} target="_blank" rel="noreferrer" data-testid="film-trailer-link" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white text-[12px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors"><Film size={16} /> Bande-annonce</a>}
                  </div>
                )}
              </div>
              {(info.length > 0 || aw) && (
                <div className="rounded-xl border border-white/10 bg-surface/50 p-6 h-fit" data-testid="film-modal-info">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-4">Fiche du film</div>
                  <dl className="space-y-3">
                    {aw && (
                      <div className="border-b border-white/5 pb-3" data-testid="film-modal-info-award">
                        <dt className="text-xs tracking-[0.15em] uppercase text-secondary2 mb-1.5">Récompense</dt>
                        <dd><AwardLabel name={aw.name} color={aw.color} size={Math.min(aw.size, 1)} className="justify-start" /></dd>
                      </div>
                    )}
                    {info.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                        <dt className="text-xs tracking-[0.15em] uppercase text-secondary2">{k}</dt>
                        <dd className="text-sm text-white text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </ModalScroll>
      )}
    </Modal>
  );
};
